import AppBusy, { type BusyHold } from "@core-ui/AppBusy";
import { useDebounce } from "@core-ui/hooks/useDebounce";
import { useCallback, useEffect, useRef } from "react";

interface SourceAuthCheck<Args extends unknown[]> {
	/** Restarts the debounce and marks the app busy until this check settles. */
	start: (...args: Args) => void;
	/** Drops a pending check and whatever is in flight: its answer is no longer wanted. */
	cancel: () => void;
}

interface SourceAuthCheckProps<Args extends unknown[]> {
	/**
	 * The request itself. `isCurrent()` answers whether this run is still the newest one — the user
	 * may have typed on while it was in flight, and a stale answer must not touch the form.
	 */
	check: (isCurrent: () => boolean, ...args: Args) => Promise<void>;
	/** Runs once the newest check settles, however it ends. Typically clears the loading state. */
	onSettled: () => void;
	delayMs?: number;
}

/**
 * Debounced credentials check shared by the storage sources (GitLab, Gitea, GitVerse).
 *
 * Every source needs the same two things around its `getUser()` call, and neither belongs in the
 * form component: a `source-auth` hold, so typing a token makes the app busy while the answer is on
 * its way, and a staleness guard, so a reply for an older token cannot overwrite fields the user has
 * retyped since. The request itself stays with the source — only these two are shared.
 */
export const useSourceAuthCheck = <Args extends unknown[]>({
	check,
	onSettled,
	delayMs = 500,
}: SourceAuthCheckProps<Args>): SourceAuthCheck<Args> => {
	const holdRef = useRef<BusyHold | null>(null);
	const versionRef = useRef(0);

	const release = useCallback(() => {
		holdRef.current?.release();
		holdRef.current = null;
	}, []);

	const { start: startDebounced, cancel: cancelDebounced } = useDebounce(
		async (version: number, ...args: Args) => {
			try {
				await check(() => versionRef.current === version, ...args);
			} finally {
				if (versionRef.current === version) {
					onSettled();
					release();
				}
			}
		},
		delayMs,
		true,
	);

	const start = useCallback(
		(...args: Args) => {
			// The hold is taken here rather than inside the debounce: the work is already inevitable
			// from the keystroke, and the app should look busy for the whole wait, not only for the
			// request. `??=` keeps a burst of keystrokes to one hold.
			holdRef.current ??= AppBusy.hold("source-auth");
			startDebounced(++versionRef.current, ...args);
		},
		[startDebounced],
	);

	const cancel = useCallback(() => {
		versionRef.current++;
		cancelDebounced();
		release();
	}, [cancelDebounced, release]);

	useEffect(
		() => () => {
			versionRef.current++;
			release();
		},
		[release],
	);

	return { start, cancel };
};
